import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CashReconciliationModal from './CashReconciliationModal';
import { posApi } from '../../../api/pos';
import { unwrapResponse } from '../../../api/client';
import { toast } from '../../../components/toast';

interface ClosingBalanceModalProps {
  onClose: () => void;
}

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default function ClosingBalanceModal({ onClose }: ClosingBalanceModalProps) {
  const navigate = useNavigate();
  const [showReconciliation, setShowReconciliation] = useState(true);
  const [showPinVerification, setShowPinVerification] = useState(false);
  const [reconciliationData, setReconciliationData] = useState<{
    countedAmount: number;
    varianceReason: string;
  } | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  // Get check-in data
  const checkInData = JSON.parse(sessionStorage.getItem('posCheckIn') || '{}');
  const openingBalanceData = JSON.parse(sessionStorage.getItem('posOpeningBalance') || '{}');

  // Fetch session data on mount so fields are ready when modal opens
  useEffect(() => {
    const loadSession = async () => {
      try {
        const counter = JSON.parse(localStorage.getItem('counter') || '{}');
        const counterId = toNumber(counter?.id ?? counter?.counterId, 0);
        if (!counterId) return;
        const res = await posApi.getLastCounterSession(counterId);
        const session = unwrapResponse<any>(res);
        setSessionData(session);
      } catch {
        // fall through — sessionFields will use local fallbacks
      } finally {
        setSessionLoading(false);
      }
    };
    loadSession();
  }, []);

  // Local fallbacks from session storage
  const openingBalance = parseFloat(openingBalanceData.amount) || 0;
  const paidInOutTransactions = JSON.parse(sessionStorage.getItem('paidInOutTransactions') || '[]');
  const paidIn = paidInOutTransactions
    .filter((t: any) => t.type === 'in')
    .reduce((sum: number, t: any) => sum + t.amount, 0);
  const paidOut = paidInOutTransactions
    .filter((t: any) => t.type === 'out')
    .reduce((sum: number, t: any) => sum + t.amount, 0);
  const completedSales = JSON.parse(sessionStorage.getItem('completedSales') || '[]');
  const cashSales = completedSales.reduce((sum: number, sale: any) => {
    const cashPayments = (sale.paymentMethods || [])
      .filter((p: any) => p.method === 'Cash')
      .reduce((total: number, p: any) => total + p.amount, 0);
    return sum + cashPayments;
  }, 0);
  const cashRefunds = completedSales.reduce((sum: number, sale: any) => {
    if (sale.total < 0) {
      const cashPayments = (sale.paymentMethods || [])
        .filter((p: any) => p.method === 'Cash')
        .reduce((total: number, p: any) => total + Math.abs(p.amount), 0);
      return sum + cashPayments;
    }
    return sum;
  }, 0);

  // Session fields — prefer API data, fall back to local storage values
  const sessionFields = {
    openingCash: toNumber(sessionData?.openingCash, openingBalance),
    salesAmount: toNumber(sessionData?.salesAmount, cashSales),
    cashIn: toNumber(sessionData?.cashIn, paidIn),
    cashOut: toNumber(sessionData?.cashOut, paidOut),
    receivedCash: toNumber(sessionData?.receivedCash, 0),
    receivedBankAmount: toNumber(sessionData?.receivedBankAmount, 0),
    salesReturnAmount: toNumber(sessionData?.salesReturnAmount, cashRefunds),
    salesRefundAmount: toNumber(sessionData?.salesRefundAmount, 0),
    adjustedCreditAmount: toNumber(sessionData?.adjustedCreditAmount, 0),
  };

  const closingCash =
    sessionFields.openingCash +
    sessionFields.salesAmount +
    sessionFields.cashIn -
    sessionFields.cashOut -
    sessionFields.receivedBankAmount -
    sessionFields.salesReturnAmount;

  const handleReconciliationConfirm = (countedAmount: number, varianceReason: string) => {
    setReconciliationData({ countedAmount, varianceReason });
    setShowReconciliation(false);
    setShowPinVerification(true);
  };

  const handlePinInput = (digit: string) => {
    if (pin.length < 6) {
      setPin(pin + digit);
      setError('');
    }
  };

  const handlePinClear = () => {
    setPin('');
    setError('');
  };

  const handlePinDelete = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  useEffect(() => {
    if (!showPinVerification) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isVerifying) return;
      if (e.key >= '0' && e.key <= '9') {
        handlePinInput(e.key);
      } else if (e.key === 'Backspace') {
        handlePinDelete();
      } else if (e.key === 'Enter' && pin.length === 6) {
        handleVerifyAndCheckOut();
      } else if (e.key === 'Escape') {
        setShowPinVerification(false);
        setShowReconciliation(true);
        setPin('');
        setError('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPinVerification, isVerifying, pin]);

  const handleVerifyAndCheckOut = async () => {
    if (pin.length !== 6) {
      setError('Please enter a 6-digit PIN');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const counter = JSON.parse(localStorage.getItem('counter') || '{}');
      const outletSession = JSON.parse(localStorage.getItem('outletSession') || '{}');
      const clientProfile = JSON.parse(localStorage.getItem('clientBusinessProfile') || '{}');

      const counterId = toNumber(counter?.id ?? counter?.counterId, 0);
      if (!counterId) {
        throw new Error('Missing counter context. Please re-check in and try again.');
      }

      // Reuse already-fetched session data; re-fetch only if unavailable
      let latestSession = sessionData;
      if (!latestSession?.id) {
        const latestSessionRes = await posApi.getLastCounterSession(counterId);
        latestSession = unwrapResponse<any>(latestSessionRes);
      }
      if (!latestSession?.id) {
        throw new Error('Counter session not found. Please re-check in and try again.');
      }

      const variance = reconciliationData
        ? reconciliationData.countedAmount - closingCash
        : 0;

      const submitableData = {
        id: latestSession?.id,
        counterId: toNumber(latestSession?.counter?.id ?? counterId, counterId),
        outletSessionId: toNumber(latestSession?.outletSession?.id ?? outletSession?.id, 0),
        userId: toNumber(latestSession?.user?.id ?? clientProfile?.userId ?? clientProfile?.UserId, 0),
        openingCash: toNumber(latestSession?.openingCash, 0),
        cashIn: toNumber(latestSession?.cashIn, 0),
        cashOut: toNumber(latestSession?.cashOut, 0),
        receivedCash: toNumber(latestSession?.receivedCash, 0),
        salesReturnAmount: toNumber(latestSession?.salesReturnAmount, 0),
        receivedBankAmount: toNumber(latestSession?.receivedBankAmount, 0),
        salesRefundAmount: toNumber(latestSession?.salesRefundAmount, 0),
        adjustedCreditAmount: toNumber(latestSession?.adjustedCreditAmount, 0),
        salesAmount: toNumber(latestSession?.salesAmount, 0),
        startTime: latestSession?.startTime ? new Date(latestSession.startTime) : new Date(),
        endTime: new Date(),
        cashShortOrExcess: toNumber(variance, 0),
        closingCash: toNumber(reconciliationData?.countedAmount, 0),
      };

      await posApi.checkOutCounter(submitableData);

      const closingData = {
        ...reconciliationData,
        closingCash,
        timestamp: new Date().toISOString(),
        cashier: checkInData.user?.name || 'Unknown',
        outlet: checkInData.outlet?.name || 'Unknown',
        counter: checkInData.counter?.name || 'Unknown',
      };

      sessionStorage.setItem('posClosingBalance', JSON.stringify(closingData));

      localStorage.removeItem('counter');
      localStorage.removeItem('counterSession');
      localStorage.removeItem('counterCheckedInInfo');
      localStorage.removeItem('pendingCheckedInOutlet');
      sessionStorage.removeItem('posCheckIn');
      sessionStorage.removeItem('posOpeningBalance');
      sessionStorage.removeItem('paidInOutTransactions');
      sessionStorage.removeItem('completedSales');
      sessionStorage.removeItem('posDrawerEvents');

      toast.success('Counter checked out successfully.');
      navigate('/pos-checkin');
    } catch (err: any) {
      const msg = err?.message || 'Failed to check out counter. Please try again.';
      toast.error(msg);
      navigate('/pos-checkin');
    } finally {
      setIsVerifying(false);
    }
  };

  const variance = reconciliationData
    ? reconciliationData.countedAmount - closingCash
    : 0;

  return (
    <>
      {showReconciliation && (
        <CashReconciliationModal
          onClose={onClose}
          onConfirm={handleReconciliationConfirm}
          sessionFields={sessionFields}
          isLoading={sessionLoading}
        />
      )}

      {showPinVerification && reconciliationData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[95vh]">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <div className="flex items-center gap-2">
                <i className="ri-shield-keyhole-line text-xl text-gray-700"></i>
                <h2 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Verify Check Out</h2>
              </div>
              <button
                onClick={() => {
                  setShowPinVerification(false);
                  setShowReconciliation(true);
                  setPin('');
                  setError('');
                }}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Cash Reconciliation Summary</p>
                    <p className="text-xs text-blue-700 mt-1">Review the reconciliation before finalizing check out</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Closing Cash:</span>
                  <span className="text-sm font-medium text-gray-900">{closingCash.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Counted Amount:</span>
                  <span className="text-sm font-medium text-gray-900">{reconciliationData.countedAmount.toFixed(2)}</span>
                </div>
                <div className="pt-3 border-t border-gray-300 flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900">Variance:</span>
                  <span className={`text-lg font-bold ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                    <i className="ri-user-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{checkInData.user?.name || 'Unknown Cashier'}</p>
                    <p className="text-xs text-gray-600">{checkInData.user?.role || 'Cashier'}</p>
                  </div>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  <p><span className="font-medium">Outlet:</span> {checkInData.outlet?.name || 'Unknown'}</p>
                  <p><span className="font-medium">Counter:</span> {checkInData.counter?.name || 'Unknown'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                  Enter Your PIN to Confirm Check Out
                </label>

                <div className="flex justify-center mb-4">
                  <div className="flex gap-2">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-12 h-12 flex items-center justify-center border-2 rounded-lg ${
                          i < pin.length ? 'border-teal-500 bg-teal-50' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {i < pin.length && (
                          <div className="w-3 h-3 bg-teal-600 rounded-full"></div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <button
                      key={num}
                      onClick={() => handlePinInput(num.toString())}
                      disabled={isVerifying}
                      className="h-14 bg-gray-100 hover:bg-gray-200 rounded-lg text-xl font-semibold text-gray-900 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    onClick={handlePinClear}
                    disabled={isVerifying}
                    className="h-14 bg-red-100 hover:bg-red-200 rounded-lg text-sm font-medium text-red-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => handlePinInput('0')}
                    disabled={isVerifying}
                    className="h-14 bg-gray-100 hover:bg-gray-200 rounded-lg text-xl font-semibold text-gray-900 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    0
                  </button>
                  <button
                    onClick={handlePinDelete}
                    disabled={isVerifying}
                    className="h-14 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-delete-back-2-line text-xl"></i>
                  </button>
                </div>

                <p className="text-xs text-gray-500 text-center mt-3">Enter your 6-digit PIN to continue</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500"></i>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>

            {/* Footer - Fixed */}
            <div className="flex gap-3 p-4 border-t bg-gray-50 flex-shrink-0">
              <button
                onClick={() => {
                  setShowPinVerification(false);
                  setShowReconciliation(true);
                  setPin('');
                  setError('');
                }}
                disabled={isVerifying}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back
              </button>
              <button
                onClick={handleVerifyAndCheckOut}
                disabled={pin.length !== 6 || isVerifying}
                className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVerifying ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Verifying...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line mr-2"></i>
                    Verify & Check Out
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
